import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Eye, Upload, Search, GripVertical, Image as ImageIcon } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';
import ImageUploadModal from '../../components/common/ImageUploadModal';


const OrganizationsPage = () => {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Upload modal
  const [uploadModal, setUploadModal] = useState(null); // { imageType, title, aspectRatio }
  // Image preview lightbox
  const [previewImage, setPreviewImage] = useState(null); // { src, label }

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await adminManagementApi.getOrganizations(); setOrgs(res.data); }
    catch (err) { console.error('Failed to load organizations:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ ID: selected.id, DisplayName: selected.displayName, Description: selected.description || '', IsActive: selected.isActive });
    setDirtyFields(new Set());
  };

  const startAdd = () => {
    setSelected(null);
    setEditing({ DisplayName: '', Description: '', IsActive: true });
    setDirtyFields(new Set(['DisplayName']));
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) { notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); }); return; }
    setEditing(null); setDirtyFields(new Set());
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const origMap = { DisplayName: selected?.displayName, Description: selected?.description || '', IsActive: selected?.isActive };
    const newDirty = new Set(dirtyFields);
    if (value !== origMap[field] || !selected) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    setSaving(true);
    try {
      if (editing.ID) { await adminManagementApi.updateOrganization(editing.ID, editing); }
      else { await adminManagementApi.createOrganization(editing); }
      const savedId = editing.ID;
      setEditing(null); setDirtyFields(new Set());
      const res = await adminManagementApi.getOrganizations();
      setOrgs(res.data);
      if (savedId) { const refreshed = res.data.find(o => o.id === savedId); if (refreshed) setSelected(refreshed); }
      else setSelected(null);
    } catch (err) { console.error('Save failed:', err); }
    finally { setSaving(false); }
  };

  const handleDelete = () => {
    if (!editing?.ID) { cancelEdit(); return; }
    notify.confirm(`Delete organization "${editing.DisplayName}"?`, async () => {
      setSaving(true);
      try { await adminManagementApi.deleteOrganization(editing.ID); setEditing(null); setSelected(null); setDirtyFields(new Set()); await load(); }
      catch (err) { console.error('Delete failed:', err); }
      finally { setSaving(false); }
    }, { onCancel: () => cancelEdit() });
  };

  const handleUpload = async (imageType, file) => {
    const orgId = editing?.ID || selected?.id;
    if (!orgId) return;
    try {
      await adminManagementApi.uploadOrgImage(orgId, imageType, file);
      const res = await adminManagementApi.getOrganizations();
      setOrgs(res.data);
      const refreshed = res.data.find(o => o.id === orgId);
      if (refreshed) setSelected(refreshed);
      setImgCacheBust(Date.now());
      // Image is saved to blob storage immediately — exit edit mode if no other dirty fields
      if (dirtyFields.size === 0) {
        setEditing(null);
        setDirtyFields(new Set());
      }
      notify.success(`${imageType === 'logo' ? 'Logo' : 'Icon'} uploaded`);
    } catch (err) {
      const msg = err.response?.data;
      const errMsg = typeof msg === 'string' ? msg : msg?.title || msg?.message || `Failed to upload ${imageType}`;
      notify.error(errMsg);
      throw new Error(errMsg);
    }
  };

  const handleDeleteImage = (imageType) => {
    const orgId = editing?.ID || selected?.id;
    if (!orgId) return;
    notify.confirm(`Remove ${imageType}? This is immediate.`, async () => {
      try {
        await adminManagementApi.deleteOrgImage(orgId, imageType);
        const res = await adminManagementApi.getOrganizations();
        setOrgs(res.data);
        const refreshed = res.data.find(o => o.id === orgId);
        if (refreshed) setSelected(refreshed);
        setImgCacheBust(Date.now());
        if (dirtyFields.size === 0) {
          setEditing(null);
          setDirtyFields(new Set());
        }
        notify.success(`${imageType === 'logo' ? 'Logo' : 'Icon'} removed`);
      } catch (err) {
        notify.error(`Failed to delete ${imageType}`);
      }
    });
  };

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0;
  const filtered = orgs.filter(o => !searchTerm || o.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) || o.description?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Image table row renderer
  const orgId = editing?.ID || selected?.id;
  const [hoverImage, setHoverImage] = useState(null);

  const IMAGE_CONFIG = {
    icon:  { outputSize: { width: 256, height: 256 } },
    logo:  { maxOutputHeight: 80, maxOutputWidth: 400 }
  };

  const [imgCacheBust, setImgCacheBust] = useState(Date.now());
  const renderImageRow = (imageType, label, hasImage, blobUrl) => {
    const config = IMAGE_CONFIG[imageType] || {};
    const imgSrc = blobUrl ? `${blobUrl}?v=${imgCacheBust}` : null;
    const sugName = selected?.displayName ? selected.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-') + `-${imageType}` : imageType;
    return (
      <tr>
        <td style={{ fontWeight: 500, padding: 'var(--spacing-sm) var(--spacing-md)', width: '80px', verticalAlign: 'middle' }}>{label}</td>
        <td style={{ padding: 'var(--spacing-sm)', width: '50px', verticalAlign: 'middle', textAlign: 'center' }}>
        </td>
        <td style={{ textAlign: 'right', padding: 'var(--spacing-sm) var(--spacing-md)', verticalAlign: 'middle' }}>
          {isEditing && !hasImage && (
            <button
              onClick={() => setUploadModal({ imageType, title: `Upload ${label}`, suggestedName: sugName, ...config })}
              title={`Upload ${label}`}
              style={{ background: 'none', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', cursor: 'pointer', color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.15s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.backgroundColor = 'var(--color-primary-light)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-gray-300)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <ImageIcon size={14} /><Upload size={12} />
            </button>
          )}
          {hasImage ? (
            <div
              style={{ position: 'relative', display: 'inline-block' }}
              onMouseEnter={() => isEditing && setHoverImage(imageType)}
              onMouseLeave={() => setHoverImage(null)}
            >
              <img
                src={imgSrc}
                alt={label}
                onClick={() => !isEditing && setPreviewImage({ src: imgSrc, label })}
                style={{
                  ...(imageType === 'icon'
                    ? { width: '40px', height: '40px', objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)' }
                    : { maxHeight: '40px', maxWidth: '120px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)' }),
                  opacity: hoverImage === imageType ? 0.4 : 1,
                  transition: 'opacity 0.15s ease',
                  cursor: isEditing ? 'default' : 'pointer'
                }}
              />
              {hoverImage === imageType && isEditing && (
                <button
                  onClick={() => handleDeleteImage(imageType)}
                  style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}
                  title={`Remove ${label}`}
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          ) : null}
        </td>
      </tr>
    );
  };

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="module-container">
      <div className="module-header">
        <div className="module-header-left">
          <Building2 size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Organizations</h1>
          <span className="module-header-meta">{orgs.length} organizations</span>
        </div>
        <div className="module-header-actions">
          {isEditing ? (
            <button className="crud-btn" onClick={handleDelete} disabled={saving} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn" onClick={startAdd} title="Add organization"><Plus size={18} /></button>
          )}
          <button className={`crud-btn ${!isEditing && !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="list-view">
        {listCollapsed ? (
          <div className="list-panel-collapsed"><button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button></div>
        ) : (
          <div className="list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search organizations..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>
            <div className="list-scroll">
              {filtered.length === 0 ? (
                <div className="task-list-empty">No organizations found</div>
              ) : filtered.map(org => (
                <button key={org.id} onClick={() => { setSelected(org); setEditing(null); setDirtyFields(new Set()); }} className={`list-item-btn ${selected?.id === org.id ? 'selected' : ''}`}>
                  <span className="list-item-primary">{org.displayName}</span>
                  <span className="list-item-secondary">{org.isActive ? 'Active' : 'Inactive'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="detail-panel">
          {(isEditing || selected) ? (
            <div className="detail-content">
              <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                <div className="col-span-24"><div className="detail-section-header">{isEditing ? (editing.ID ? 'Edit Organization' : 'New Organization') : 'Organization Details'}</div></div>
                <div className="form-group col-span-8">
                  <label>Display Name</label>
                  {isEditing ? (
                    <input className={`form-input${dirtyFields.has('DisplayName') ? ' dirty' : ''}`} value={editing.DisplayName} onChange={e => handleFieldChange('DisplayName', e.target.value)} autoFocus />
                  ) : (
                    <input className="form-input" value={selected.displayName} readOnly />
                  )}
                </div>
                <div className="form-group col-span-12">
                  <label>Description</label>
                  {isEditing ? (
                    <input className={`form-input${dirtyFields.has('Description') ? ' dirty' : ''}`} value={editing.Description} onChange={e => handleFieldChange('Description', e.target.value)} />
                  ) : (
                    <input className="form-input" value={selected.description || ''} readOnly />
                  )}
                </div>
                <div className="form-group col-span-4">
                  <label>Active</label>
                  {isEditing ? (
                    <select className={`form-input${dirtyFields.has('IsActive') ? ' dirty' : ''}`} value={editing.IsActive ? 'true' : 'false'} onChange={e => handleFieldChange('IsActive', e.target.value === 'true')}>
                      <option value="true">Yes</option><option value="false">No</option>
                    </select>
                  ) : (
                    <input className="form-input" value={selected.isActive ? 'Yes' : 'No'} readOnly />
                  )}
                </div>
              </div>

              {/* Images table — only available after record is saved */}
              {!orgId && isEditing && !editing.ID && (
                <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                  <div className="col-span-24"><div className="detail-section-header">Images</div></div>
                  <div className="col-span-24" style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)', padding: '8px 0' }}>
                    Save the organization first, then edit to add logo and icon images.
                  </div>
                </div>
              )}
              {orgId && (
                <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                  <div className="col-span-24"><div className="detail-section-header">Images</div></div>
                  <div className="col-span-24">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                          {renderImageRow('logo', 'Logo', !!selected?.logoUrl, selected?.logoUrl)}
                          {renderImageRow('icon', 'Icon', !!selected?.iconUrl, selected?.iconUrl)}
                        </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="dev-detail-empty"><Eye size={48} /><p>Select an organization to view details</p><p className="hint">Or click + to create a new one</p></div>
          )}
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={!!uploadModal}
        onClose={() => setUploadModal(null)}
        onUpload={async (file) => { await handleUpload(uploadModal.imageType, file); }}
        title={uploadModal?.title || 'Upload Image'}
        suggestedName={uploadModal?.suggestedName || ''}
        aspectRatio={uploadModal?.aspectRatio}
        outputSize={uploadModal?.outputSize}
        maxOutputHeight={uploadModal?.maxOutputHeight}
        maxOutputWidth={uploadModal?.maxOutputWidth}
      />

      {/* Image Preview Lightbox */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'pointer' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', background: 'repeating-conic-gradient(#d4d4d4 0% 25%, white 0% 50%) 50% / 16px 16px', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-sm)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            <img src={previewImage.src} alt={previewImage.label} style={{ display: 'block', maxWidth: '80vw', maxHeight: '80vh' }} />
            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)', fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', background: 'white', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>
              {previewImage.label} — actual size
            </div>
            <button
              onClick={() => setPreviewImage(null)}
              style={{ position: 'absolute', top: '-12px', right: '-12px', background: 'white', border: '1px solid var(--color-gray-300)', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage;
